import React from 'react';
import NavigationHeader from '../components/NavigationHeader';
import HeroSection from '../components/HeroSection';
import ProgramFeatures from '../components/ProgramFeatures';
import Training from '../components/Training';
import Academies from '../components/Academies';
import LanguageSection from '../components/LanguageSection';
import Schedule from '../components/Schedule';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, Clock, Activity, GraduationCap, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { programFeatures, trainingData, academies, scheduleData, clinicImages } from '../mock';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const ClinicPage = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  const heroData = {
    title: t('clinic.heroTitle'),
    subtitle: t('clinic.heroSubtitle'),
    backgroundImage: clinicImages.hero,
    ctaButtons: [
      { text: t('clinic.viewPrices'), type: "primary" },
      { text: t('clinic.signUpNow'), type: "secondary" }
    ]
  };

  return (
    <div className="min-h-screen">
      <NavigationHeader />
      <HeroSection data={heroData} />
      
      {/* Program Overview with Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Image */}
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={clinicImages.gallery[0]} 
                alt="High Performance Training"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Right: Stats */}
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
                {t('clinic.programOverview')}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {t('clinic.programOverviewDesc')}
              </p>

              <div className="grid grid-cols-2 gap-4">
                <Card className="p-6 text-center bg-gradient-to-br from-green-50 to-white border-2 border-green-200">
                  <Users className="h-10 w-10 mx-auto mb-3 text-green-700" />
                  <p className="text-sm text-gray-600 mb-1">{t('clinic.ages')}</p>
                  <p className="text-2xl font-black text-gray-900">11-17</p>
                </Card>
                <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200">
                  <Clock className="h-10 w-10 mx-auto mb-3 text-blue-700" />
                  <p className="text-sm text-gray-600 mb-1">{t('clinic.duration')}</p>
                  <p className="text-2xl font-black text-gray-900">1-4 {t('clinic.weeks')}</p>
                </Card>
                <Card className="p-6 text-center bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200">
                  <Activity className="h-10 w-10 mx-auto mb-3 text-orange-700" />
                  <p className="text-sm text-gray-600 mb-1">{t('clinic.intensity')}</p>
                  <p className="text-2xl font-black text-gray-900">4.5/5</p>
                </Card>
                <Card className="p-6 text-center bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200">
                  <GraduationCap className="h-10 w-10 mx-auto mb-3 text-purple-700" />
                  <p className="text-sm text-gray-600 mb-1">{t('clinic.english')}</p>
                  <p className="text-2xl font-black text-gray-900">4.5 {t('clinic.hrsDay')}</p>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ProgramFeatures features={programFeatures} />
      <Training data={trainingData} />
      <LanguageSection />
      <Academies academies={academies} />
      <Schedule data={scheduleData} />
      
      {/* Pricing & Dates Section */}
      <section className="py-20 bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              {t('clinic.pricingDates')}
            </h2>
            <p className="text-xl text-green-100 max-w-3xl mx-auto">
              {t('clinic.pricingDatesDesc')}
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <Card className="p-6 bg-white text-gray-900 hover:shadow-2xl transition-all duration-300">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-black mb-2">1 {t('clinic.week')}</h3>
                <div className="text-4xl font-black text-green-600 mb-1">£1,895</div>
                <p className="text-xs text-gray-600">{t('clinic.fullBoardIncluded')}</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('clinic.dailyTrainingSessions')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('clinic.englishLessons')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('clinic.allActivitiesIncluded')}</span>
                </li>
              </ul>
            </Card>

            <Card className="p-6 bg-white text-gray-900 border-4 border-yellow-400 transform scale-105 shadow-2xl">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-xs font-black">
                  {t('clinic.mostPopular')}
                </div>
              </div>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-black mb-2">2 {t('clinic.weeks2')}</h3>
                <div className="text-4xl font-black text-green-600 mb-1">£2,895</div>
                <p className="text-xs text-gray-600">{t('clinic.fullBoardIncluded')}</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('clinic.dailyTrainingSessions')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('clinic.englishLessons')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('clinic.stadiumExcursion')}</span>
                </li>
              </ul>
            </Card>

            <Card className="p-6 bg-white text-gray-900 hover:shadow-2xl transition-all duration-300">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-black mb-2">3 {t('clinic.weeks2')}</h3>
                <div className="text-4xl font-black text-green-600 mb-1">£4,495</div>
                <p className="text-xs text-gray-600">{t('clinic.fullBoardIncluded')}</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('clinic.dailyTrainingSessions')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('clinic.stadiumExcursion')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('clinic.extendedEvaluationReport')}</span>
                </li>
              </ul>
            </Card>

            <Card className="p-6 bg-white text-gray-900 hover:shadow-2xl transition-all duration-300">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-black mb-2">4 {t('clinic.weeks2')}</h3>
                <div className="text-4xl font-black text-green-600 mb-1">£5,695</div>
                <p className="text-xs text-gray-600">{t('clinic.fullBoardIncluded')}</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('clinic.stadiumExcursion')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('clinic.comprehensiveEvaluation')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('clinic.certificateCompletion')}</span>
                </li>
              </ul>
            </Card>
          </div>

          {/* Start Dates */}
          <Card className="p-8 bg-white/10 backdrop-blur-lg border-white/20">
            <h3 className="text-2xl font-black mb-2 text-center">{t('clinic.startDates2026')}</h3>
            <p className="text-sm text-center text-orange-300 font-semibold mb-6">{t('prices.limitedAvailability')}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-orange-400/40 hover:border-orange-300 transition-all">
                <div className="text-lg font-black mb-1">29 June</div>
                <div className="inline-flex items-center gap-1 bg-orange-500/20 text-orange-300 text-xs font-bold px-2 py-0.5 rounded-full mt-1">
                  9 {t('prices.spotsLeft')}
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-orange-400/40 hover:border-orange-300 transition-all">
                <div className="text-lg font-black mb-1">6 July</div>
                <div className="inline-flex items-center gap-1 bg-orange-500/20 text-orange-300 text-xs font-bold px-2 py-0.5 rounded-full mt-1">
                  9 {t('prices.spotsLeft')}
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-red-500/40 opacity-60">
                <div className="text-lg font-black mb-1 line-through">13 July</div>
                <div className="inline-flex items-center gap-1 bg-red-500/20 text-red-300 text-xs font-bold px-2 py-0.5 rounded-full mt-1">
                  {t('prices.soldOut')}
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-red-500/40 opacity-60">
                <div className="text-lg font-black mb-1 line-through">20 July</div>
                <div className="inline-flex items-center gap-1 bg-red-500/20 text-red-300 text-xs font-bold px-2 py-0.5 rounded-full mt-1">
                  {t('prices.soldOut')}
                </div>
              </div>
            </div>
          </Card>

          <div className="text-center mt-12">
            <Link to="/signup">
              <Button 
                size="lg" 
                className="bg-white text-green-700 hover:bg-gray-100 font-bold text-lg px-10 py-6"
              >
                {t('clinic.signUpNow')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Photo Gallery */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              {t('clinic.experienceClinic')}
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              {t('clinic.experienceClinicDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {clinicImages.gallery.slice(1).map((image, index) => (
              <div 
                key={index}
                className="relative overflow-hidden rounded-xl group cursor-pointer h-80"
              >
                <img 
                  src={image} 
                  alt={`Clinic Training ${index + 1}`}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-green-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ClinicPage;
